import { useTags } from "data-hooks/company/tags/tags.service";
import { Tag } from "data-hooks/company/tags/tags.type";
import { formatTagName } from "data-hooks/company/tags/tags.utils";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { MultiValue } from "react-select";
import { ModalPopup } from "../ModalPopup/ModalPopup";
import { Popup } from "../Popup/Popup";
import { SelectInputData, SelectInputField } from "../SelectInputField/SelectInputField";
import { Text } from "../Text/Text";

const getInitialTags = (tags: Tag[], value: string[]) => {
  const initialTags: Tag[] = [];
  for (const tag of tags) {
    if (value.includes(tag.id)) {
      initialTags.push(tag);
    }
  }
  return initialTags;
};

type Props = {
  id: string;
  value: string[];
  onChange: (tags: Tag[]) => void;
  title?: string;
  label?: string;
  actionTitle?: string;
  propertyId?: string | string[];
  dataCy?: string;
};

export const TagsSelectPopup = ({ id, value, onChange, title, label, actionTitle, propertyId, dataCy }: Props) => {
  const { tags } = useTags({ propertyId });

  const [selectedTags, setSelectedTags] = useState<Tag[]>(getInitialTags(tags ?? [], value));

  useEffect(() => {
    setSelectedTags(getInitialTags(tags ?? [], value));
  }, [value, tags]);

  const tagOptions: SelectInputData[] =
    tags?.map((tag) => {
      return new SelectInputData(tag.id, formatTagName(tag));
    }) ?? [];

  const handleChange = (options: MultiValue<SelectInputData>) => {
    if (Array.isArray(options)) {
      const newTags: Tag[] = [];
      for (const selected of options) {
        const tag = tags?.find((tag) => tag.id === selected.value);
        if (tag) {
          newTags.push(tag);
        }
      }
      setSelectedTags(newTags);
    } else {
      setSelectedTags([]);
    }
  };

  const handleConfirm = () => {
    Popup.remove(id);
    onChange(selectedTags);
  };

  return (
    <ModalPopup id={id} title={title ?? t("main.selectTags")} actionTitle={actionTitle ?? t("main.confirm")} onActionClicked={handleConfirm}>
      <>
        {label && (
          <Text textAlign="left" marginBottom={16}>
            {label}
          </Text>
        )}
        <SelectInputField
          width="100%"
          menuPortalTarget="popup"
          placeholder={t("main.selectTags")}
          dataCy={dataCy}
          multi
          useCustomSort
          options={tagOptions}
          value={selectedTags.map((tag) => tag.id)}
          onChange={handleChange}
        />
      </>
    </ModalPopup>
  );
};
